/**
 * dataSourceMutations.ts
 *
 * The ONLY place that writes to both `dataSources` and `syncQueue` for datasource ops.
 * Every write is atomic — both stores update in one IDB transaction or neither does.
 *
 * IDB RESILIENCE:
 *   - If IDB is unavailable or quota-full, each mutating function calls the API
 *     directly so the operation is never silently lost.
 *   - loadDataSourcesMutation is hydration-only; it skips silently when IDB is
 *     unavailable since context already holds the data.
 *   - patchDataSourcePropertyMutation needs the full existing properties map to
 *     build the merged update; it skips gracefully when IDB is unavailable —
 *     context still shows the optimistic update, and the server will be correct
 *     on next full fetch.
 *
 * Replaces boardContext datasource mutation methods:
 *   refreshNotes / setDataSource  → loadDataSourcesMutation()   (hydration, no queue entry)
 *   updateDataSource              → updateDataSourceMutation()
 *   patchDataSourceProperty       → patchDataSourcePropertyMutation()
 */

import { appDB, emitIDBChange } from "../db/client";
import {
  toDataSourceRecord,
  updateDataSourceInDB,
} from "../stores/dataSourcesStore";
import { countPendingOps } from "../stores/syncQueueStore";
import { setPendingCount } from "../stores/syncMetaStore";
import { isIDBAvailable, setIDBStatus, isQuotaError } from "../idbStatus";
import { postWithAuth, deleteWithAuth } from "@/lib/api-helpers";
import type { DatabaseSource, BoardProperty } from "@/types/board";
import type {
  DataSourceUpdatePayload,
  PropertyCreatePayload,
  PropertyDeletePayload,
  PropertySchemaUpdatePayload,
  SchemaReorderPayload,
  DatasourcePageSettingsUpdatePayload,
  DatasourceSettingsUpdatePayload,
} from "../db/schema";

// ─── Internal helper ──────────────────────────────────────────────

function handleQuotaError(mutationName: string): void {
  setIDBStatus("quota_full");
  console.warn(
    `%c[${mutationName}] IDB quota exceeded mid-session — falling back to direct API`,
    "color: #fb923c; font-weight: bold;"
  );
}

// ─────────────────────────────────────────────────────────────────
// INITIAL HYDRATION (from API — no queue entry)
// Replaces: boardContext.setDataSource() / boardContext.refreshNotes()
// ─────────────────────────────────────────────────────────────────

/**
 * Load data sources from the API into IDB.
 * Marks all as isSync: 1 — already exists in MongoDB.
 * Does NOT add to syncQueue.
 *
 * If IDB is unavailable, skip silently — context already holds the data.
 */
export async function loadDataSourcesMutation(
  dataSources: DatabaseSource[],
  workspaceId: string
): Promise<void> {
  if (!isIDBAvailable()) return;

  try {
    const records = dataSources.map((ds) =>
      toDataSourceRecord(ds, ds.workspaceId ?? workspaceId, 1)
    );
    await appDB.dataSources.bulkPut(records);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("loadDataSourcesMutation");
      return; // No API fallback — data came FROM the API; context already has it
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// UPDATE DATA SOURCE
// Replaces: boardContext.updateDataSource()
// ─────────────────────────────────────────────────────────────────

/**
 * Merge partial updates into a datasource in IDB and enqueue a DATASOURCE_UPDATE op.
 * The SyncWatcher will call the API once online.
 */
export async function updateDataSourceMutation(
  dataSourceId: string,
  updates: Partial<DatabaseSource>,
  workspaceId: string,
  blockId: string
): Promise<void> {
  const payload: DataSourceUpdatePayload = {
    dataSourceId,
    workspaceId,
    blockId,
    updates,
  };

  // API shape: { blockId, dataSourceId, ...updates }
  const apiPayload = { blockId, dataSourceId, ...updates };

  // ── Direct API path ───────────────────────────────────────────────────────
  if (!isIDBAvailable()) {
    await postWithAuth("/api/database/update", apiPayload);
    return;
  }

  // ── IDB path ─────────────────────────────────────────────────────────────
  try {
    await appDB.transaction("rw", [appDB.dataSources, appDB.syncQueue], async () => {
      await updateDataSourceInDB(dataSourceId, updates);
      await appDB.syncQueue.add({
        id: crypto.randomUUID(),
        operationType: "DATASOURCE_UPDATE",
        payload,
        createdAt: Date.now(),
        isSync: 0 as 0,
        attempts: 0,
        lastAttemptAt: null,
        syncError: null,
        noteId: null,
        workspaceId,
      });
    });

    const count = await countPendingOps();
    await setPendingCount(count);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("updateDataSourceMutation");
      await postWithAuth("/api/database/update", apiPayload);
      return;
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// CREATE DATASOURCE PROPERTY
// Replaces: direct postWithAuth("/api/database/createProperty") call
// ─────────────────────────────────────────────────────────────────

/**
 * Optimistically writes a new property into the datasource in IDB and
 * enqueues a DATASOURCE_PROPERTY_CREATE op. The SyncWatcher will call
 * /api/database/create-property once online.
 */
export async function createPropertyMutation(
  dataSourceId: string,
  propertyId: string,
  propertySchema: BoardProperty,
  apiPayload: Omit<PropertyCreatePayload, "workspaceId">,
  workspaceId: string
): Promise<void> {
  const fullPayload: PropertyCreatePayload = { ...apiPayload, workspaceId };

  // ── Direct API path ───────────────────────────────────────────────────────
  if (!isIDBAvailable()) {
    await postWithAuth("/api/database/create-property", fullPayload);
    return;
  }

  // ── IDB path ─────────────────────────────────────────────────────────────
  try {
    await appDB.transaction("rw", [appDB.dataSources, appDB.syncQueue], async () => {
      const existing = await appDB.dataSources.get(dataSourceId);
      if (existing) {
        // insertAfterPropertyId (duplicate flow): mirror the server's placement.
        let nextProperties: Record<string, any>;
        const anchor = fullPayload.insertAfterPropertyId;
        if (anchor && existing.properties?.[anchor]) {
          nextProperties = {};
          for (const [key, value] of Object.entries(existing.properties)) {
            nextProperties[key] = value;
            if (key === anchor) nextProperties[propertyId] = propertySchema;
          }
        } else {
          nextProperties = { ...existing.properties, [propertyId]: propertySchema };
        }
        await appDB.dataSources.put({
          ...existing,
          properties: nextProperties,
          isSync: 0 as 0,
          localUpdatedAt: Date.now(),
        });
      }

      await appDB.syncQueue.add({
        id: crypto.randomUUID(),
        operationType: "DATASOURCE_PROPERTY_CREATE",
        payload: fullPayload,
        createdAt: Date.now(),
        isSync: 0 as 0,
        attempts: 0,
        lastAttemptAt: null,
        syncError: null,
        noteId: null,
        workspaceId,
      });
    });

    const count = await countPendingOps();
    await setPendingCount(count);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("createPropertyMutation");
      await postWithAuth("/api/database/create-property", fullPayload);
      return;
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// PATCH DATA SOURCE PROPERTY
// Replaces: boardContext.patchDataSourceProperty()
// ─────────────────────────────────────────────────────────────────

/**
 * Safely patches a single property inside a datasource's properties map.
 * Reads current record fresh from IDB — safe for stale-closure situations.
 * Enqueues a DATASOURCE_UPDATE op so watcher syncs the change.
 *
 * NOTE: This mutation reads existing IDB state to build the full merged
 * properties map before writing. If IDB is unavailable we skip gracefully —
 * the component's optimistic context update remains visible, and the server
 * will be authoritative on next full fetch.
 */
export async function patchDataSourcePropertyMutation(
  dataSourceId: string,
  propertyId: string,
  patch: Partial<Record<string, any>>,
  workspaceId: string,
  blockId: string
): Promise<void> {
  // Cannot build the merged payload without reading existing IDB state.
  // Skip gracefully — context already shows the optimistic update.
  if (!isIDBAvailable()) return;

  try {
    await appDB.transaction("rw", [appDB.dataSources, appDB.syncQueue], async () => {
      const existing = await appDB.dataSources.get(dataSourceId);
      if (!existing?.properties?.[propertyId]) return;

      const updatedProperty = { ...existing.properties[propertyId], ...patch };
      const updatedProperties = { ...existing.properties, [propertyId]: updatedProperty };

      await appDB.dataSources.put({
        ...existing,
        properties: updatedProperties,
        isSync: 0 as 0,
        localUpdatedAt: Date.now(),
      });

      const payload: DataSourceUpdatePayload = {
        dataSourceId,
        workspaceId,
        blockId,
        updates: { properties: updatedProperties },
      };

      await appDB.syncQueue.add({
        id: crypto.randomUUID(),
        operationType: "DATASOURCE_UPDATE",
        payload,
        createdAt: Date.now(),
        isSync: 0 as 0,
        attempts: 0,
        lastAttemptAt: null,
        syncError: null,
        noteId: null,
        workspaceId,
      });
    });

    const count = await countPendingOps();
    await setPendingCount(count);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("patchDataSourcePropertyMutation");
      // Swallow — same reasoning as the unavailable case above.
      return;
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// DELETE DATASOURCE PROPERTY
// Replaces: direct deleteWithAuth("/api/database/deleteProperty") call
// ─────────────────────────────────────────────────────────────────

/**
 * Strips exactly the three fields that make a property a synced mirror —
 * the local echo of the server's dotted `$unset` for a "detached" counterpart.
 */
function detachedCopy(property: BoardProperty): BoardProperty {
  const {
    syncedPropertyId: _sid,
    syncedPropertyName: _sname,
    twoWayRelation: _two,
    ...rest
  } = property;
  return rest as BoardProperty;
}

/**
 * Optimistically removes a property from the datasource in IDB and
 * enqueues a DATASOURCE_PROPERTY_DELETE op.
 *
 * TWO-WAY RELATIONS: the server cascades to the counterpart property as part of
 * the same request, so the counterpart is updated locally in the SAME
 * transaction — otherwise it lingers stale until the next full fetch, and
 * forever while offline. Still exactly one queue op: the cascade is the
 * server's. `apiPayload.counterpartPolicy` carries the user's choice from the
 * confirmation dialog and is mirrored here:
 *   "delete" (or absent) → drop the counterpart property
 *   "detach"             → keep it, clear its synced-mirror fields
 * Absent means "delete" so callers that never ask keep today's behaviour.
 */
export async function deletePropertyMutation(
  dataSourceId: string,
  propertyId: string,
  apiPayload: Omit<PropertyDeletePayload, "workspaceId">,
  workspaceId: string
): Promise<void> {
  // counterpartPolicy rides inside apiPayload, so this spread threads it into
  // the queued op payload AND both direct-API bodies below.
  const fullPayload: PropertyDeletePayload = { ...apiPayload, workspaceId };
  const detachCounterpart = apiPayload.counterpartPolicy === "detach";

  // ── Direct API path ───────────────────────────────────────────────────────
  if (!isIDBAvailable()) {
    await deleteWithAuth("/api/database/delete-property", {
      body: JSON.stringify(fullPayload),
    });
    return;
  }

  // ── IDB path ─────────────────────────────────────────────────────────────
  try {
    await appDB.transaction("rw", [appDB.dataSources, appDB.syncQueue], async () => {
      const existing = await appDB.dataSources.get(dataSourceId);
      if (existing && existing.properties) {
        // Read the property BEFORE dropping it — its relation fields are the
        // only way to find the counterpart the server will cascade-delete.
        const deleted: BoardProperty | undefined = existing.properties[propertyId];
        const counterpartId =
          deleted?.type === "relation" && deleted.syncedPropertyId && deleted.linkedDatabaseId
            ? deleted.syncedPropertyId
            : "";
        const linkedDataSourceId = String(deleted?.linkedDatabaseId ?? "");
        const isSelfRelation = !!counterpartId && linkedDataSourceId === String(dataSourceId);

        const { [propertyId]: _, ...remainingProperties } = existing.properties;

        // VERIFIED-MIRROR GUARD: a property schema carries no `id` of its own —
        // identity is the record key — so a stale syncedPropertyId looks exactly
        // like a live one and an unguarded removal could destroy an unrelated
        // column. The counterpart must point back at THIS property on THIS
        // datasource. For a self-relation it is read from the post-drop snapshot.
        const linked =
          counterpartId && !isSelfRelation
            ? await appDB.dataSources.get(linkedDataSourceId)
            : undefined;
        const counterpart: BoardProperty | undefined = counterpartId
          ? (isSelfRelation ? remainingProperties : linked?.properties)?.[counterpartId]
          : undefined;
        const isVerifiedMirror =
          counterpart?.type === "relation" &&
          counterpart.syncedPropertyId === propertyId &&
          String(counterpart.linkedDatabaseId) === String(dataSourceId);

        if (counterpartId && !isVerifiedMirror) {
          console.warn(
            `[deletePropertyMutation] Counterpart ${counterpartId} on ${linkedDataSourceId} is not a verified mirror of ${propertyId} — leaving it untouched`
          );
        }

        // SELF-relation: both sides live on this record — apply the counterpart's
        // fate in the single put below. Reassigning an existing key keeps its
        // position in the properties map.
        if (isVerifiedMirror && isSelfRelation && counterpart) {
          if (detachCounterpart) {
            remainingProperties[counterpartId] = detachedCopy(counterpart);
          } else {
            delete remainingProperties[counterpartId];
          }
        }

        await appDB.dataSources.put({
          ...existing,
          properties: remainingProperties,
          isSync: 0 as 0,
          localUpdatedAt: Date.now(),
        });

        // CROSS-datasource: drop (or detach) the counterpart on the linked record,
        // but leave its isSync alone — the SERVER owns that write. Marking it 0
        // would have the watcher push a second op the API rejects with
        // "Property not found in data source".
        if (isVerifiedMirror && !isSelfRelation && linked?.properties && counterpart) {
          const { [counterpartId]: __, ...withoutCounterpart } = linked.properties;
          await appDB.dataSources.put({
            ...linked,
            properties: detachCounterpart
              ? { ...linked.properties, [counterpartId]: detachedCopy(counterpart) }
              : withoutCounterpart,
            localUpdatedAt: Date.now(),
          });
        }
      }

      await appDB.syncQueue.add({
        id: crypto.randomUUID(),
        operationType: "DATASOURCE_PROPERTY_DELETE",
        payload: fullPayload,
        createdAt: Date.now(),
        isSync: 0 as 0,
        attempts: 0,
        lastAttemptAt: null,
        syncError: null,
        noteId: null,
        workspaceId,
      });
    });

    const count = await countPendingOps();
    await setPendingCount(count);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("deletePropertyMutation");
      await deleteWithAuth("/api/database/delete-property", {
        body: JSON.stringify(fullPayload),
      });
      return;
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// UPDATE DATASOURCE PROPERTY SCHEMA
// Replaces: direct postWithAuth("/api/database/updatePropertySchema") call
// ─────────────────────────────────────────────────────────────────

/**
 * Optimistically updates a property configuration in the datasource in IDB
 * and enqueues a DATASOURCE_PROPERTY_SCHEMA_UPDATE op.
 */
export async function updatePropertySchemaMutation(
  dataSourceId: string,
  propertyId: string,
  payload: Omit<PropertySchemaUpdatePayload, "workspaceId">,
  workspaceId: string
) {
  if (!dataSourceId || !propertyId) return;

  const fullPayload: PropertySchemaUpdatePayload = { ...payload, workspaceId };

  // ── Direct API path ───────────────────────────────────────────────────────
  if (!isIDBAvailable()) {
    await postWithAuth("/api/database/update-property-schema", fullPayload);
    return;
  }

  // ── IDB path ─────────────────────────────────────────────────────────────
  try {
    await appDB.transaction("rw", [appDB.dataSources, appDB.syncQueue], async () => {
      const dsRecord = await appDB.dataSources.get(dataSourceId);
      if (dsRecord) {
        const currentProps = dsRecord.properties || {};
        const currentProperty = currentProps[propertyId] || {};

        const updatedProperty = {
          ...currentProperty,
          name: payload.newName,
          type: payload.type,
          ...(payload.options !== undefined && { options: payload.options }),
          ...(payload.groups !== undefined && { groups: payload.groups }),
          ...(payload.showProperty !== undefined && { showProperty: payload.showProperty }),
          ...(payload.isVisibleInSlack !== undefined && { isVisibleInSlack: payload.isVisibleInSlack }),
          ...(payload.formula !== undefined && { formula: payload.formula }),
          ...(payload.formulaReturnType !== undefined && { formulaReturnType: payload.formulaReturnType }),
          ...(payload.relationLimit !== undefined && { relationLimit: payload.relationLimit }),
          ...(payload.twoWayRelation !== undefined && { twoWayRelation: payload.twoWayRelation }),
          ...(payload.linkedDatabaseId !== undefined && { linkedDatabaseId: payload.linkedDatabaseId }),
          ...(payload.syncedPropertyId !== undefined && { syncedPropertyId: payload.syncedPropertyId }),
          ...(payload.syncedPropertyName !== undefined && { syncedPropertyName: payload.syncedPropertyName }),
          ...(payload.specialProperty !== undefined && { specialProperty: payload.specialProperty }),
          ...(payload.rollup !== undefined && { rollup: payload.rollup }),
          ...(payload.githubPrConfig !== undefined && { githubPrConfig: payload.githubPrConfig }),
          ...(payload.numberFormat !== undefined && { numberFormat: payload.numberFormat }),
          ...(payload.decimalPlaces !== undefined && { decimalPlaces: payload.decimalPlaces }),
          ...(payload.showAs !== undefined && { showAs: payload.showAs }),
          ...(payload.progressColor !== undefined && { progressColor: payload.progressColor }),
          ...(payload.progressDivideBy !== undefined && { progressDivideBy: payload.progressDivideBy }),
          ...(payload.showNumberText !== undefined && { showNumberText: payload.showNumberText }),
          ...(payload.timeFormat !== undefined && { timeFormat: payload.timeFormat }),
          ...(payload.timeZone !== undefined && { timeZone: payload.timeZone }),
          ...(payload.prefix !== undefined && { prefix: payload.prefix }),
          ...(payload.icon !== undefined && { icon: payload.icon }),
        };

        await appDB.dataSources.update(dataSourceId, {
          properties: {
            ...currentProps,
            [propertyId]: updatedProperty,
          } as Record<string, BoardProperty>,
          localUpdatedAt: Date.now(),
          isSync: 0 as 0,
        });
      }

      await appDB.syncQueue.add({
        id: crypto.randomUUID(),
        operationType: "DATASOURCE_PROPERTY_SCHEMA_UPDATE",
        payload: fullPayload,
        createdAt: Date.now(),
        isSync: 0 as 0,
        attempts: 0,
        lastAttemptAt: null,
        syncError: null,
        noteId: null,
        workspaceId,
      });
    });

    const count = await countPendingOps();
    await setPendingCount(count);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("updatePropertySchemaMutation");
      await postWithAuth("/api/database/update-property-schema", fullPayload);
      return;
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// UPDATE DATASOURCE PAGE SETTINGS (custom layout — applies to ALL pages of the datasource)
// → /api/database/page-settings
// Stored directly on the datasource at `pageSettings.<key>`.
// ─────────────────────────────────────────────────────────────────

export interface UpdateDatasourcePageSettingsOptions {
  dataSourceId: string;
  blockId: string;
  workspaceId: string;
  settings: any;
}

export interface UpdateDatasourceSettingsOptions {
  dataSourceId: string;
  blockId: string;
  workspaceId: string;
  settings: any;
}

/**
 * Optimistically writes pageSettings on the datasource (merged into `pageSettings`)
 * and enqueues a DATASOURCE_PAGE_SETTINGS_UPDATE op. SyncWatcher pushes it to
 * POST /api/database/page-settings.
 */
export async function updateDatasourcePageSettingsMutation(
  options: UpdateDatasourcePageSettingsOptions
): Promise<void> {
  const { dataSourceId, blockId, workspaceId, settings } = options;

  const payload: DatasourcePageSettingsUpdatePayload = {
    dataSourceId,
    blockId,
    workspaceId,
    settings,
  };

  // ── Direct API path ───────────────────────────────────────────────────────
  if (!isIDBAvailable()) {
    await postWithAuth("/api/database/page-settings", { dataSourceId, blockId, settings });
    return;
  }

  // ── IDB path ─────────────────────────────────────────────────────────────
  try {
    await appDB.transaction("rw", [appDB.dataSources, appDB.syncQueue], async () => {
      const existing = await appDB.dataSources.get(dataSourceId);
      if (existing) {
        const prev = (existing.pageSettings ?? {}) as Record<string, any>;
        await appDB.dataSources.put({
          ...existing,
          // Merge so partial updates don't wipe siblings.
          pageSettings: { ...prev, ...settings },
          isSync: 0 as 0,
          localUpdatedAt: Date.now(),
        });
      }

      await appDB.syncQueue.add({
        id: crypto.randomUUID(),
        operationType: "DATASOURCE_PAGE_SETTINGS_UPDATE",
        payload,
        createdAt: Date.now(),
        isSync: 0 as 0,
        attempts: 0,
        lastAttemptAt: null,
        syncError: null,
        noteId: null,
        workspaceId,
      });
    });

    const count = await countPendingOps();
    await setPendingCount(count);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("updateDatasourcePageSettingsMutation");
      await postWithAuth("/api/database/page-settings", { dataSourceId, blockId, settings });
      return;
    }
    throw err;
  }
}

/**
 * Optimistically writes datasource settings (merged into `settings`)
 * and enqueues a DATASOURCE_SETTINGS_UPDATE op. SyncWatcher pushes it to
 * POST /api/database/datasource/settings.
 */
export async function updateDatasourceSettingsMutation(
  options: UpdateDatasourceSettingsOptions
): Promise<void> {
  const { dataSourceId, blockId, workspaceId, settings } = options;

  const payload: DatasourceSettingsUpdatePayload = {
    dataSourceId,
    blockId,
    workspaceId,
    settings,
  };

  // ── Direct API path ───────────────────────────────────────────────────────
  if (!isIDBAvailable()) {
    await postWithAuth("/api/database/datasource/settings", { dataSourceId, blockId, settings });
    return;
  }

  // ── IDB path ─────────────────────────────────────────────────────────────
  try {
    await appDB.transaction("rw", [appDB.dataSources, appDB.syncQueue], async () => {
      const existing = await appDB.dataSources.get(dataSourceId);
      if (existing) {
        const prev = (existing.settings ?? {}) as Record<string, any>;
        await appDB.dataSources.put({
          ...existing,
          settings: { ...prev, ...settings },
          isSync: 0 as 0,
          localUpdatedAt: Date.now(),
        });
      }

      await appDB.syncQueue.add({
        id: crypto.randomUUID(),
        operationType: "DATASOURCE_SETTINGS_UPDATE",
        payload,
        createdAt: Date.now(),
        isSync: 0 as 0,
        attempts: 0,
        lastAttemptAt: null,
        syncError: null,
        noteId: null,
        workspaceId,
      });
    });

    const count = await countPendingOps();
    await setPendingCount(count);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("updateDatasourceSettingsMutation");
      await postWithAuth("/api/database/datasource/settings", { dataSourceId, blockId, settings });
      return;
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// REORDER A DATASOURCE'S PROPERTIES
// → /api/database/re-order-schema
// The server rewrites the schema's key order, which is what the order
// survives a reload as — so the local copy is rewritten the same way.
// ─────────────────────────────────────────────────────────────────

export async function reorderSchemaMutation(payload: SchemaReorderPayload) {
  const { dataSourceId, blockId, viewId, order, workspaceId } = payload;
  if (!dataSourceId || !blockId || !Array.isArray(order)) return;

  const body = { dataSourceId, blockId, viewId, order };

  // ── Direct API path ───────────────────────────────────────────────────────
  if (!isIDBAvailable()) {
    await postWithAuth("/api/database/re-order-schema", body);
    return;
  }

  // ── IDB path ─────────────────────────────────────────────────────────────
  try {
    await appDB.transaction("rw", [appDB.dataSources, appDB.syncQueue], async () => {
      const dsRecord = await appDB.dataSources.get(dataSourceId);
      if (dsRecord?.properties) {
        const current = dsRecord.properties as Record<string, BoardProperty>;
        const reordered: Record<string, BoardProperty> = {};
        for (const id of order) {
          if (current[id]) reordered[id] = current[id];
        }
        for (const [id, prop] of Object.entries(current)) {
          if (!reordered[id]) reordered[id] = prop;
        }
        await appDB.dataSources.update(dataSourceId, {
          properties: reordered,
          localUpdatedAt: Date.now(),
          isSync: 0 as 0,
        });
      }

      await appDB.syncQueue.add({
        id: crypto.randomUUID(),
        operationType: "DATASOURCE_SCHEMA_REORDER",
        payload,
        createdAt: Date.now(),
        isSync: 0 as 0,
        attempts: 0,
        lastAttemptAt: null,
        syncError: null,
        noteId: null,
        workspaceId,
      });
    });

    const count = await countPendingOps();
    await setPendingCount(count);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("reorderSchemaMutation");
      await postWithAuth("/api/database/re-order-schema", body);
      return;
    }
    throw err;
  }
}
